import { useI18n } from 'next-localization';
import classNames from 'classnames';
import { useMemo, useState } from 'react';
import useWindowDimensions from 'hooks/useWindowDimensions';
import useHelpers from 'hooks/helpers';
import { standardFormat } from '@/constants/dateFormats';
import CustomScroll from 'components/Functional/CustomScroll';
import { TabData } from '@/store/fundDetails/typings';

const FundDetailsDiscretePerformanceTable = ({ tabData }: TabData) => {
  const [gradientPosition, setGradientPosition] = useState('gradient-right');
  const { deviceType } = useWindowDimensions();
  const { localizeDate } = useHelpers();
  const { t } = useI18n();

  const theadData = useMemo(() => {
    const firstRow = tabData?.performanceSet?.[0];
    return firstRow?.values?.map((column) => column?.performanceTimePeriod);
  }, [tabData]);

  const tbodyData = useMemo(() => {
    return tabData?.performanceSet?.map((row) => ({
      header: row?.performanceLabel,
      data: row?.values?.map((column) => ({
        header: column?.performanceTimePeriod,
        value: column?.value || t('notAvailableShortMessage'),
      })),
    }));
  }, [tabData, t]);

  const isScrollable = useMemo(() => {
    if (tabData === null) return false;
    if (deviceType === 'mobile' || deviceType === 'tablet') {
      return tabData.performanceSet[0].values.length >= 2;
    } else {
      return tabData.performanceSet[0].values.length > 4;
    }
  }, [tabData, deviceType]);

  const footnote = useMemo(() => {
    const lastRelevantDate = tabData?.lastRelevantDate;
    if (!lastRelevantDate) return;
    return `${t('fundDetailsPerformanceCumulativeFootnote')} ${localizeDate(
      lastRelevantDate,
      standardFormat
    )}`;
  }, [tabData, t]);

  if (tbodyData && tbodyData.length) {
    return (
      <>
        <div className="position-relative">
          <div
            id="discretePerformanceWrapper"
            className={classNames('abrdn-table', {
              scrollable: isScrollable,
              [gradientPosition]: gradientPosition,
            })}
          >
            <table
              id="discretePerformanceTable"
              aria-label={t('fundDetailsPerformanceDiscreteTitle')}
              className="not-striped performance-table"
            >
              <thead>
                <tr>
                  <th scope="col" className="sticky-column" />
                  {theadData.map((header) => (
                    <th key={header} scope="col">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tbodyData.map((row) => (
                  <tr key={row.header}>
                    <th scope="row" className="sticky-column">
                      {row.header}
                    </th>
                    {row.data.map((data) => (
                      <td key={data.header}>{data.value}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {footnote && <div className="mt-1 footnote text-grey">{footnote}</div>}

        {isScrollable && (
          <CustomScroll
            deviceType={deviceType}
            wrapperId="discretePerformanceWrapper"
            tableId="discretePerformanceTable"
            changeGradientPosition={setGradientPosition}
          />
        )}
      </>
    );
  } else {
    return <div className="body-default">{t('noResultsMessage')}</div>;
  }
};

export default FundDetailsDiscretePerformanceTable;
